import { ReactElement } from 'react';
import { useRouter } from 'next/router';
import { getImageProps } from 'next/image';

import { Breakpoints } from '@hooks/useBreakpoint';
import { usePrefetchQuickAddProducts } from '@adapters/bigcommerce/hooks/product/usePrefetchQuickAddProducts';
import { FEATURE_FLAG_QUICK_ADD } from '@config/feature-flag';
import { colourKeyForVariantSku } from '@utils/algolia/colourKeyForVariantSku';
import { isQuickAddEligible } from '@utils/algolia/isQuickAddEligible';
import Heading from '@components/ui/Heading';
import CtaLinks from '_components/CtaLinks';
import ProductStrip from './ProductStrip';
import type { ImageProductStripData } from '@adapters/prismic/transforms/imageProductStrip';

import styles from './ImageProductStrip.module.scss';

export interface ImageProductStripProps extends ImageProductStripData {
  priority?: boolean;
}

// The hotspot GA4 events carry a `tab_name`; this component has no tabs.
const NO_TAB_NAME = '';

const ImageProductStrip = ({
  heading,
  desktopImage,
  mobileImage,
  stripHeading,
  ctas,
  products,
  coOpBrand,
  priority = false,
}: ImageProductStripProps): ReactElement => {
  const { asPath } = useRouter();

  usePrefetchQuickAddProducts(
    FEATURE_FLAG_QUICK_ADD
      ? products
          .filter(({ product, variantSku }) =>
            isQuickAddEligible(
              product,
              colourKeyForVariantSku(product, variantSku)
            )
          )
          .map(({ product }) => product.parentSku)
      : []
  );

  const interactionLocation = asPath.split('?')[0];

  const {
    props: { srcSet: desktopSrcSet, sizes: desktopSizes },
  } = getImageProps({
    src: desktopImage.url,
    alt: desktopImage.alt ?? '',
    fill: true,
    sizes: '100vw',
    priority,
    fetchPriority: priority ? 'high' : undefined,
  });
  const { props: mobileImageProps } = getImageProps({
    src: mobileImage.url,
    alt: desktopImage.alt || mobileImage.alt || '',
    fill: true,
    sizes: '100vw',
    priority,
    fetchPriority: priority ? 'high' : undefined,
    className: styles.image,
  });

  return (
    <section className={styles.imageProductStrip}>
      {heading && (
        <div className={styles.headingContainer}>
          <Heading semanticLevel="h2" styleLevel="2" className={styles.heading}>
            {heading}
          </Heading>
        </div>
      )}
      <div className={styles.mediaContainer}>
        <div className={styles.imageWrapper}>
          <picture>
            <source
              media={`(min-width: ${Breakpoints.md}px)`}
              srcSet={desktopSrcSet}
              sizes={desktopSizes}
            />
            <img {...mobileImageProps} />
          </picture>
          <div className={styles.imageOverlay} aria-hidden="true" />
        </div>

        <ProductStrip
          products={products}
          header={
            <div className={styles.stripHeader}>
              {stripHeading && (
                <Heading
                  semanticLevel={heading ? 'h3' : 'h2'}
                  styleLevel="2"
                  className={styles.stripHeading}
                >
                  {stripHeading}
                </Heading>
              )}
              <CtaLinks
                ctas={ctas}
                interactionLocation={interactionLocation}
                tabName={NO_TAB_NAME}
                coOpBrand={coOpBrand}
              />
            </div>
          }
          interactionLocation={interactionLocation}
          tabName={NO_TAB_NAME}
          coOpBrand={coOpBrand}
        />
      </div>
    </section>
  );
};

export default ImageProductStrip;
